/**
 * TagsSection (TRA-939): the team's run tags.
 *
 * The one place to see the whole vocabulary with usage, and the only
 * surface for rename/delete. Both apply across EVERY run carrying the
 * tag (renames merge into an existing tag when the names collide, matching
 * the server's case-insensitive vocabulary rules), so each goes through a
 * dialog that says so. The mutations live here, not in the dialogs: a
 * dialog can close mid-flight, and the answer must still land (see
 * ConfirmDialog). Under staff impersonation the list is read-only: the
 * API refuses the writes, and a destructive rename or delete must never
 * look available when it is not.
 */

import { Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import type { SettingsSectionProps } from '@/components/settings/sections'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EYEBROW } from '@/components/ui/eyebrow'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { useDeleteTag, useRenameTag, useTagVocabulary } from '@/hooks/useLabels'
import { countLabel } from '@/lib/utils'

import { RowAction } from './RowAction'
import { SettingsList, SettingsListRow } from './SettingsList'
import { SettingsPageHeader } from './SettingsPageHeader'
import { SettingsSection } from './SettingsSection'

interface TagRef {
  tag: string
  count: number
}

export function TagsSection({ section }: SettingsSectionProps): React.JSX.Element {
  const { impersonatingTeamId } = useTeam()
  const readOnly = impersonatingTeamId !== null
  const vocabulary = useTagVocabulary()
  const renameTag = useRenameTag()
  const deleteTag = useDeleteTag()
  const [renaming, setRenaming] = useState<TagRef | null>(null)
  const [deleting, setDeleting] = useState<TagRef | null>(null)
  const [renameError, setRenameError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const tags = vocabulary.data?.tags ?? []
  const counts = vocabulary.data?.counts ?? {}

  const submitRename = (from: string, to: string): void => {
    setRenameError(null)
    renameTag.mutate(
      { data: { from_tag: from, to_tag: to } },
      {
        onSuccess: () => {
          toast.success(`Renamed "${from}" to "${to}" on every run carrying it`)
          setRenaming(null)
        },
        onError: (error) => {
          setRenameError(getApiErrorDetail(error) ?? 'Could not rename the tag.')
        },
      },
    )
  }

  const submitDelete = (tag: string): void => {
    setDeleteError(null)
    deleteTag.mutate(
      { data: { tag } },
      {
        onSuccess: () => {
          toast.success(`Removed "${tag}" from every run carrying it`)
          setDeleting(null)
        },
        onError: (error) => {
          setDeleteError(getApiErrorDetail(error) ?? 'Could not delete the tag.')
        },
      },
    )
  }

  return (
    <div>
      <SettingsPageHeader section={section} />
      <SettingsSection
        title="Run tags"
        description="Both actions apply to every run carrying the tag, and a rename into an existing tag merges the two. Tag a run from the Trajectories table or the review composer."
      >
        {vocabulary.isError ? (
          <p className="text-sm text-destructive" role="alert">
            Tags could not be loaded. Reload to retry.
          </p>
        ) : vocabulary.isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : tags.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No tags yet. Tag a run from the Trajectories table or the review composer and it
            will appear here.
          </p>
        ) : (
          <SettingsList>
            {tags.map((tag) => (
              <TagRow
                key={tag}
                tag={tag}
                count={counts[tag] ?? 0}
                readOnly={readOnly}
                onRename={() => {
                  setRenameError(null)
                  setRenaming({ tag, count: counts[tag] ?? 0 })
                }}
                onDelete={() => {
                  setDeleteError(null)
                  setDeleting({ tag, count: counts[tag] ?? 0 })
                }}
              />
            ))}
          </SettingsList>
        )}
      </SettingsSection>

      {renaming !== null && (
        <RenameTagDialog
          tag={renaming.tag}
          isPending={renameTag.isPending}
          error={renameError}
          onSubmit={(to) => {
            submitRename(renaming.tag, to)
          }}
          onClose={() => {
            setRenaming(null)
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
        title="Delete tag"
        description={
          deleting === null ? null : (
            <>
              Remove <strong>{deleting.tag}</strong> from {countLabel(deleting.count, 'run')}?
              The runs are kept.
            </>
          )
        }
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={deleteTag.isPending}
        error={deleteError}
        onConfirm={() => {
          if (deleting) submitDelete(deleting.tag)
        }}
      />
    </div>
  )
}

function TagRow({
  tag,
  count,
  readOnly,
  onRename,
  onDelete,
}: {
  tag: string
  count: number
  readOnly: boolean
  onRename: () => void
  onDelete: () => void
}): React.JSX.Element {
  return (
    <SettingsListRow className="min-h-12">
      <span
        title={tag}
        className="inline-flex h-5 max-w-[280px] items-center whitespace-nowrap border border-border bg-muted px-1.5 font-mono text-[11px] text-foreground"
      >
        <span className="truncate">{tag}</span>
      </span>
      <span className="flex-1" />
      <span className={`${EYEBROW} tabular-nums normal-case`}>{countLabel(count, 'run')}</span>
      {!readOnly && (
        <span className="flex items-center gap-0.5">
          <RowAction label={`Rename tag ${tag}`} icon={Pencil} onClick={onRename} />
          <RowAction label={`Delete tag ${tag}`} icon={Trash2} destructive onClick={onDelete} />
        </span>
      )}
    </SettingsListRow>
  )
}

/**
 * Mounted only while open, so each rename starts from the tag's current
 * name with no leftover draft or error. Closing is refused mid-flight.
 */
function RenameTagDialog({
  tag,
  isPending,
  error,
  onSubmit,
  onClose,
}: {
  tag: string
  isPending: boolean
  error: string | null
  onSubmit: (to: string) => void
  onClose: () => void
}): React.JSX.Element {
  const [draft, setDraft] = useState(tag)
  const to = draft.trim()
  const canSubmit = to !== '' && to !== tag && !isPending

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isPending) onClose()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rename tag</DialogTitle>
          <DialogDescription>
            The new name applies to every run carrying <strong>{tag}</strong>. Renaming into an
            existing tag merges the two.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) onSubmit(to)
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="rename-tag">New name</Label>
            <Input
              id="rename-tag"
              className="font-mono"
              value={draft}
              autoFocus
              autoComplete="off"
              onChange={(e) => {
                setDraft(e.target.value)
              }}
            />
            {error !== null && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
